import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';

import {
  AnalyticsService,
  ZonePlausibilityNotice,
} from '../../analytics/analytics.service';

export type ZonePlausibilityBannerMode = 'global' | 'contextual';

/**
 * Banner für unplausible AUTO-Zonen. Schwester des Zonen-Drift-Banners, füllt
 * genau dessen Lücke (source='auto', wo der Drift nichts meldet).
 *
 * - mode='global' (Default): lädt offene Notices selbst.
 * - mode='contextual': erwartet vorgefilterte Notices via [notices].
 *
 * Kein Dialog: die Warnung heilt sich, sobald die Zonen wieder plausibel sind.
 * Der Nutzer kann sie wegklicken (dismiss) oder über den Deeplink korrigieren.
 * Warn-Ästhetik bewusst identisch zu den bestehenden Zonen-Warnungen
 * (zone-deviation-banner, cp-test-detail .zones-warning).
 */
@Component({
  selector: 'app-zone-plausibility-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink, MatIconModule, MatButtonModule, TranslateModule],
  template: `
    @if (visibleNotices.length > 0) {
      <div class="zp-banner" [class.zp-banner--coach]="visibleNotices.length > 1">
        <mat-icon class="zp-icon">warning</mat-icon>

        <div class="zp-text">
          @if (visibleNotices.length === 1) {
            <strong>
              @if (visibleNotices[0].athlete_name && !isSelfNotice(visibleNotices[0])) {
                {{ 'diagnostics.zone_plausibility.banner.title' | translate }} —
                {{ visibleNotices[0].athlete_name }}
              } @else {
                {{ 'diagnostics.zone_plausibility.banner.title' | translate }}
              }
            </strong>
            <span class="zp-body">{{ 'diagnostics.zone_plausibility.banner.body' | translate }}</span>
            <ul class="zp-issues">
              @for (issue of visibleNotices[0].issues; track issue) {
                <li>{{ 'diagnostics.zone_plausibility.issue.' + issue | translate }}</li>
              }
            </ul>
          } @else {
            <strong>
              {{ 'diagnostics.zone_plausibility.banner.title_coach' | translate:{ n: visibleNotices.length } }}
            </strong>
            <ul class="zp-list">
              @for (n of visibleNotices; track n.id) {
                <li>{{ n.athlete_name || ('User #' + n.user_id) }}</li>
              }
            </ul>
          }
          <a class="zp-settings-link" routerLink="/analyse/diagnostik/trainingsbereiche"
             [queryParams]="{ tab: 'eintraege' }">
            {{ 'diagnostics.zone_plausibility.banner.settings_link' | translate }}
          </a>
        </div>

        <span class="grow"></span>
        <button mat-icon-button
                (click)="dismiss(visibleNotices[0])"
                [attr.aria-label]="'diagnostics.zone_plausibility.banner.dismiss' | translate">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    }
  `,
  styles: [`
    .zp-banner {
      display: flex;
      align-items: flex-start;
      gap: 0.6rem;
      padding: 0.6rem 0.85rem;
      margin: 0.5rem 0;
      background: #fff8e1;
      color: #8d6e00;
      border-left: 4px solid #ef6c00;
      border-radius: 6px;
      font-size: var(--fs-sm);
    }
    .zp-banner--coach { background: #e3f2fd; color: #0d47a1; border-left-color: #1565c0; }
    .zp-icon { color: #ef6c00; flex-shrink: 0; margin-top: 0.1rem; }
    .zp-banner--coach .zp-icon { color: #1565c0; }
    .zp-text { flex: 1; min-width: 0; }
    .zp-text strong { display: block; }
    .zp-body { display: block; margin-top: 0.15rem; color: inherit; }
    .zp-issues, .zp-list { margin: 0.35rem 0 0; padding-left: 1.25rem; }
    .zp-settings-link {
      display: inline-block; margin-top: 0.4rem;
      font-size: var(--fs-xs); color: inherit;
      text-decoration: underline; cursor: pointer;
    }
    .grow { flex: 1; }
  `],
})
export class ZonePlausibilityBannerComponent implements OnInit {
  private readonly analytics = inject(AnalyticsService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() mode: ZonePlausibilityBannerMode = 'global';
  @Input() notices: ZonePlausibilityNotice[] | null = null;
  @Input() currentUserId: number | null = null;

  visibleNotices: ZonePlausibilityNotice[] = [];

  ngOnInit(): void {
    if (this.mode === 'contextual') {
      this.visibleNotices = this.notices ?? [];
      return;
    }
    this.analytics.getZonePlausibilityNotices('open').subscribe({
      next: (resp) => {
        this.visibleNotices = resp.notices ?? [];
        this.cdr.markForCheck();
      },
      error: () => {
        this.visibleNotices = [];
        this.cdr.markForCheck();
      },
    });
  }

  dismiss(notice: ZonePlausibilityNotice): void {
    // Optimistisch ausblenden; ein Fehlschlag bringt sie beim nächsten Laden zurück.
    this.visibleNotices = this.visibleNotices.filter((n) => n.id !== notice.id);
    this.cdr.markForCheck();
    this.analytics.dismissZonePlausibility(notice.id).subscribe({ error: () => {} });
  }

  isSelfNotice(n: ZonePlausibilityNotice): boolean {
    return this.currentUserId != null && n.user_id === this.currentUserId;
  }
}
